import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { isAdminOrSuperAdmin } from '../utils/auth';
import '../App.css';

interface Field {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  description?: string;
  picture?: string;
  map?: string;
  website?: string;
  phoneNumber?: string;
  email?: string;
  hoursOfOperation?: string;
  parkingInformation?: string;
  collectionId?: string;
  collection?: { id: string; name: string } | null;
}

interface FieldCollection {
  id: string;
  name: string;
  description?: string;
  fieldCount?: number;
}

const Fields: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [fields, setFields] = useState<Field[]>([]);
  const [collections, setCollections] = useState<FieldCollection[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCollectionId, setSelectedCollectionId] = useState<string>('');
  const [viewMode, setViewMode] = useState<'fields' | 'collections'>('fields');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [editingField, setEditingField] = useState<Field | null>(null);
  const [editingCollection, setEditingCollection] = useState<FieldCollection | null>(null);

  useEffect(() => {
    fetchFields();
    fetchCollections();
  }, []);

  const fetchFields = async () => {
    try {
      const response = await api.get('/fields', { params: { page: '1', limit: '100' } });
      setFields(response.data.fields || response.data || []);
    } catch (error) {
      console.error('Error fetching fields:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCollections = async () => {
    try {
      const response = await api.get('/fields/collections/all');
      setCollections(response.data || []);
    } catch (error) {
      console.error('Error fetching collections:', error);
    }
  };

  const filteredFields = fields.filter(field => {
    const matchesSearch = !searchTerm || 
      field.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      field.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
      field.city.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCollection = !selectedCollectionId || field.collectionId === selectedCollectionId;
    
    return matchesSearch && matchesCollection;
  });

  const handleDeleteField = async (fieldId: string) => {
    if (!window.confirm('Are you sure you want to delete this field?')) {
      return;
    }
    try {
      await api.delete(`/fields/${fieldId}`);
      fetchFields();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to delete field');
    }
  };

  const handleDeleteCollection = async (collectionId: string) => {
    if (!window.confirm('Are you sure you want to delete this collection? Fields in this collection will be unassigned.')) {
      return;
    }
    try {
      await api.delete(`/fields/collections/${collectionId}`);
      fetchCollections();
      fetchFields();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to delete collection');
    }
  };

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  const isAdmin = isAdminOrSuperAdmin(user?.profileType);

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Fields & Facilities</h1>
        {isAdmin && (
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              className="btn btn-secondary"
              onClick={() => setViewMode(viewMode === 'fields' ? 'collections' : 'fields')}
            >
              {viewMode === 'fields' ? 'View Collections' : 'View Fields'}
            </button>
            {viewMode === 'fields' ? (
              <button className="btn btn-primary" onClick={() => {
                setEditingField(null);
                setShowCreateModal(true);
              }}>
                Create Field
              </button>
            ) : (
              <button className="btn btn-primary" onClick={() => {
                setEditingCollection(null);
                setShowCollectionModal(true);
              }}>
                Create Collection
              </button>
            )}
          </div>
        )}
      </div>

      {viewMode === 'fields' ? (
        <>
          <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Search fields..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ flex: 1, minWidth: '200px', padding: '8px' }}
            />
            {collections.length > 0 && (
              <select
                value={selectedCollectionId}
                onChange={(e) => setSelectedCollectionId(e.target.value)}
                style={{ padding: '8px', minWidth: '200px' }}
              >
                <option value="">All Collections</option>
                {collections.map(collection => (
                  <option key={collection.id} value={collection.id}>
                    {collection.name} ({collection.fieldCount || 0} fields)
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
            {filteredFields.map(field => (
              <div key={field.id} className="card">
                {field.picture && (
                  <img
                    src={field.picture}
                    alt={field.name}
                    style={{ width: '100%', height: '200px', objectFit: 'cover', borderRadius: '8px 4px 0 0', marginBottom: '15px' }}
                  />
                )}
                <h3 style={{ marginTop: 0 }}>{field.name}</h3>
                <p style={{ color: '#757575', margin: '5px 0' }}>
                  {field.address}<br />
                  {field.city}, {field.state} {field.zipCode}
                </p>
                {field.collection && (
                  <p style={{ margin: '5px 0' }}>
                    <span className="badge badge-info">{field.collection.name}</span>
                  </p>
                )}
                {field.description && (
                  <p style={{ color: '#757575', fontSize: '14px', margin: '10px 0' }}>
                    {field.description.substring(0, 100)}{field.description.length > 100 ? '...' : ''}
                  </p>
                )}
                <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                  <Link to={`/fields/${field.id}`} className="btn btn-sm btn-primary" style={{ flex: 1 }}>
                    View Details
                  </Link>
                  {isAdmin && (
                    <>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => {
                          setEditingField(field);
                          setShowCreateModal(true);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteField(field.id)}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {filteredFields.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#757575' }}>
              {searchTerm || selectedCollectionId ? 'No fields found matching your criteria.' : 'No fields created yet.'}
            </div>
          )}
        </>
      ) : (
        <>
          {isAdmin && (
            <p style={{ color: '#757575', marginBottom: '15px' }}>
              Collections allow you to group related fields together for easier management.
            </p>
          )}
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
            {collections.map(collection => (
              <div key={collection.id} className="card">
                <h3 style={{ marginTop: 0 }}>{collection.name}</h3>
                {collection.description && (
                  <p style={{ color: '#757575', margin: '10px 0' }}>{collection.description}</p>
                )}
                <p style={{ margin: '10px 0' }}>
                  <strong>{collection.fieldCount || 0}</strong> field{collection.fieldCount !== 1 ? 's' : ''}
                </p>
                {isAdmin && (
                  <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => {
                        setEditingCollection(collection);
                        setShowCollectionModal(true);
                      }}
                      style={{ flex: 1 }}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDeleteCollection(collection.id)}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {collections.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#757575' }}>
              No collections created yet.
            </div>
          )}
        </>
      )}

      {showCreateModal && (
        <FieldModal
          field={editingField}
          collections={collections}
          onClose={() => {
            setShowCreateModal(false);
            setEditingField(null);
          }}
          onSuccess={() => {
            fetchFields();
            setShowCreateModal(false);
            setEditingField(null);
          }}
        />
      )}

      {showCollectionModal && (
        <CollectionModal
          collection={editingCollection}
          onClose={() => {
            setShowCollectionModal(false);
            setEditingCollection(null);
          }}
          onSuccess={() => {
            fetchCollections();
            setShowCollectionModal(false);
            setEditingCollection(null);
          }}
        />
      )}
    </div>
  );
};

// Field Modal Component
const FieldModal: React.FC<{
  field: Field | null;
  collections: FieldCollection[];
  onClose: () => void;
  onSuccess: () => void;
}> = ({ field, collections, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: field?.name || '',
    address: field?.address || '',
    city: field?.city || '',
    state: field?.state || '',
    zipCode: field?.zipCode || '',
    description: field?.description || '',
    picture: field?.picture || '',
    map: field?.map || '',
    website: field?.website || '',
    phoneNumber: field?.phoneNumber || '',
    email: field?.email || '',
    hoursOfOperation: field?.hoursOfOperation || '',
    parkingInformation: field?.parkingInformation || '',
    collectionId: field?.collectionId || ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [picturePreview, setPicturePreview] = useState<string>('');
  const [mapPreview, setMapPreview] = useState<string>('');

  useEffect(() => {
    if (field?.picture) setPicturePreview(field.picture);
    if (field?.map) setMapPreview(field.map);
  }, [field]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'picture' | 'map') => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError('Image size must be less than 10MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        if (type === 'picture') {
          setFormData({ ...formData, picture: base64String });
          setPicturePreview(base64String);
        } else {
          setFormData({ ...formData, map: base64String });
          setMapPreview(base64String);
        }
        setError('');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (field) {
        await api.put(`/fields/${field.id}`, formData);
      } else {
        await api.post('/fields', formData);
      }
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save field');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1200,
      overflow: 'auto',
      padding: '20px'
    }}>
      <div className="card" style={{ maxWidth: '800px', width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2>{field ? 'Edit Field' : 'Create New Field'}</h2>
          <button className="btn btn-secondary" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Field Name *</label>
            <input type="text" name="name" value={formData.name} onChange={handleChange} required />
          </div>

          <div className="form-group">
            <label>Collection</label>
            <select name="collectionId" value={formData.collectionId} onChange={handleChange}>
              <option value="">No Collection</option>
              {collections.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Address *</label>
            <input type="text" name="address" value={formData.address} onChange={handleChange} required />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
            <div className="form-group">
              <label>City *</label>
              <input type="text" name="city" value={formData.city} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>State *</label>
              <input type="text" name="state" value={formData.state} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Zip Code *</label>
              <input type="text" name="zipCode" value={formData.zipCode} onChange={handleChange} required />
            </div>
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea name="description" value={formData.description} onChange={handleChange} rows={4} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div className="form-group">
              <label>Field Picture</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange(e, 'picture')}
              />
              {picturePreview && (
                <img src={picturePreview} alt="Preview" style={{ width: '100%', marginTop: '10px', maxHeight: '200px', objectFit: 'cover' }} />
              )}
            </div>
            <div className="form-group">
              <label>Field Map</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange(e, 'map')}
              />
              {mapPreview && (
                <img src={mapPreview} alt="Map Preview" style={{ width: '100%', marginTop: '10px', maxHeight: '200px', objectFit: 'cover' }} />
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Website</label>
            <input type="url" name="website" value={formData.website} onChange={handleChange} placeholder="https://..." />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div className="form-group">
              <label>Phone Number</label>
              <input type="tel" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} />
            </div>
          </div>

          <div className="form-group">
            <label>Hours of Operation</label>
            <textarea name="hoursOfOperation" value={formData.hoursOfOperation} onChange={handleChange} rows={3} />
          </div>

          <div className="form-group">
            <label>Parking Information</label>
            <textarea name="parkingInformation" value={formData.parkingInformation} onChange={handleChange} rows={3} />
          </div>

          {error && <div className="alert alert-error">{error}</div>}

          <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : field ? 'Update Field' : 'Create Field'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Collection Modal Component
const CollectionModal: React.FC<{
  collection: FieldCollection | null;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ collection, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: collection?.name || '',
    description: collection?.description || ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (collection) {
        await api.put(`/fields/collections/${collection.id}`, formData);
      } else {
        await api.post('/fields/collections', formData);
      }
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save collection');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1200,
      overflow: 'auto',
      padding: '20px'
    }}>
      <div className="card" style={{ maxWidth: '500px', width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2>{collection ? 'Edit Collection' : 'Create New Collection'}</h2>
          <button className="btn btn-secondary" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Collection Name *</label>
            <input type="text" name="name" value={formData.name} onChange={handleChange} required />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea name="description" value={formData.description} onChange={handleChange} rows={4} />
          </div>

          {error && <div className="alert alert-error">{error}</div>}

          <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : collection ? 'Update Collection' : 'Create Collection'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Fields;

